import React, { useState, useEffect } from 'react';
import './Landing.css';

const DOMAINS = [
  'Health', 'Finance', 'Career', 'Goals',
  'Habits', 'Relationships', 'Family', 'Events',
  'Sleep', 'Skills', 'Decisions', 'Context',
];

const STEPS = [
  { icon: '💬', num: '01', title: 'Tell it things', desc: 'Chat naturally in English or Korean. Every fact becomes a structured atom in your personal knowledge graph.' },
  { icon: '🔍', num: '02', title: 'Ask anything', desc: 'Ask "Should I take this job?" or "How are my goals?" — questions grounded in your actual life data.' },
  { icon: '⚛️', num: '03', title: 'MeTTa reasoning', desc: 'Hyperon MeTTa runs symbolic inference over your knowledge graph across 8 life domains.' },
  { icon: '📊', num: '04', title: 'See the chain', desc: 'Every answer shows its reasoning chain — which facts were used and what confidence was reached.' },
  { icon: '💡', num: '05', title: 'Stay proactive', desc: 'The insight engine surfaces patterns automatically: goal drift, correlations, anomalies.' },
];

const FEATURES = [
  { icon: '🕸️', title: 'Knowledge Graph', desc: 'Facts stored as typed atoms with truth values — the same probabilistic model as OpenCog AtomSpace.' },
  { icon: '⚛️', title: 'MeTTa Reasoning', desc: 'Queries run through Hyperon MeTTa — SingularityNET\'s symbolic AI engine. Transparent and auditable.' },
  { icon: '🔐', title: 'Fully Private', desc: 'Your graph lives on your device. No cloud sync, no telemetry. PIN-protected, you own your data.' },
  { icon: '🌐', title: 'Multilingual', desc: 'English and Korean detected automatically. Responses match the language you write in.' },
  { icon: '📄', title: 'Document Ingest', desc: 'Upload PDFs, DOCX, or Markdown. Preview extracted atoms before committing to the graph.' },
  { icon: '🔗', title: 'Connector Ready', desc: 'Google Calendar and bank CSV connect your events and transactions to the reasoning engine.' },
];

export default function Landing({ onEnter, onShowReport }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => { setVisible(true); }, []);

  return (
    <div className={`landing ${visible ? 'landing--visible' : ''}`}>

      {/* ── Fixed navbar ── */}
      <nav className="lp-nav">
        <div className="lp-nav-brand">
          <div className="lp-nav-icon">A</div>
          <span className="lp-nav-name">
            ASI:Mind
          </span>
          <span className="lp-nav-version">v0.1 · Proof of Concept</span>
        </div>

        <div className="lp-nav-links">
          <button className="lp-nav-link" onClick={onEnter}>Sign in</button>
          {onShowReport && (
            <button className="lp-nav-link" onClick={onShowReport}>Dev Report</button>
          )}
          <button className="lp-nav-cta" onClick={onEnter}>Get started</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-content">
          <div className="lp-badge">
            <span className="lp-badge-dot" />
            Powered by OpenCog Hyperon · SingularityNET open source
          </div>

          <h1 className="lp-headline">
            Your life, reasoned<br />by symbolic AI
          </h1>

          <p className="lp-subheadline">
            ASI:Mind builds a private knowledge graph from everything you tell it —
            then answers your hardest personal questions with transparent, auditable
            reasoning grounded in your actual data.
          </p>

          <div className="lp-hero-actions">
            <button className="lp-cta-primary" onClick={onEnter}>
              Start for free
              <span className="lp-cta-arrow">›</span>
            </button>
            <a href="#how" className="lp-cta-secondary">See how it works</a>
          </div>

          <div className="lp-domains">
            {DOMAINS.map(d => (
              <span key={d} className="lp-domain-chip">{d}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="lp-how" id="how">
        <div className="lp-how-header">
          <h2 className="lp-how-title">How it works</h2>
          <p className="lp-how-sub">Five steps from raw conversation to reasoned, auditable insight.</p>
        </div>

        <div className="lp-steps-row">
          {STEPS.map(s => (
            <div key={s.num} className="lp-step">
              <div className="lp-step-bubble">
                {s.icon}
                <span className="lp-step-num-badge">{s.num}</span>
              </div>
              <div className="lp-step-title">{s.title}</div>
              <p className="lp-step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section className="lp-features">
        <div className="lp-features-inner">
          <div className="lp-features-header">
            <div className="lp-section-label">Features</div>
            <h2 className="lp-section-title">Built on symbolic AI, not just LLMs</h2>
          </div>
          <div className="lp-feature-grid">
            {FEATURES.map(f => (
              <div key={f.title} className="lp-feature-card">
                <div className="lp-feature-icon">{f.icon}</div>
                <div className="lp-feature-title">{f.title}</div>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Privacy ── */}
      <section className="lp-privacy">
        <div className="lp-privacy-inner">
          <div className="lp-privacy-icon">🔐</div>
          <div>
            <div className="lp-privacy-title">Your data never leaves your device</div>
            <p className="lp-privacy-desc">
              The knowledge graph is stored entirely in local memory. The optional PIN is hashed
              in your browser and never transmitted. LLM calls go to your configured provider —
              not a shared cloud. In Phase 3, only a cryptographic hash is anchored to ASI:Chain.
            </p>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="lp-cta-section">
        <div className="lp-cta-inner">
          <h2 className="lp-cta-title">Ready to know yourself?</h2>
          <p className="lp-cta-sub">No sign-up. No cloud. Just you and your knowledge graph.</p>
          <button className="lp-cta-primary lp-cta-large" onClick={onEnter}>
            Open ASI:Mind
            <span className="lp-cta-arrow">›</span>
          </button>
          <div className="lp-tech-note">
            <span className="lp-tech-tag">Hyperon MeTTa</span>
            ·
            <span className="lp-tech-tag">SingularityNET</span>
            ·
            <span className="lp-tech-tag">OpenCog AtomSpace</span>
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <span className="lp-logo-asi">ASI:</span><span className="lp-logo-mind">Mind</span>
        <span className="lp-footer-sep">·</span>
        <span style={{ color: '#1e293b' }}>know yourself</span>
        <span className="lp-footer-sep">·</span>
        <span style={{ color: '#1e293b' }}>Phase 1 · SingularityNET Proof-of-Concept</span>
      </footer>
    </div>
  );
}
